<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { useConfirm } from "@/shared/ui/alert-dialog";
import {
  getApplicationDocuments,
  uploadApplicationDocument,
  deleteApplicationDocument,
  updateApplicationDocument,
} from "@/entities/application";
import { getDirectoryItem } from "@/entities/directory";
import { extractItems } from "@/shared/serviceResponse";
import { downloadFileSecure } from "@/shared/lib/downloadFile";
import { toast } from "@/shared/store/alertStore";

import AttachmentItem from "@/shared/ui/upload/AttachmentItem.vue";
import { Cascader } from "@/shared/ui/cascader";
import { Spinner } from "@/shared/ui/spinner";
import { Empty } from "@/shared/ui/empty";
import Button from "@/shared/ui/button/Button.vue";
import AiCheckBanner from "../components/ai-check/AiCheckBanner.vue";
import AiCheckDialog from "../components/ai-check/AiCheckDialog.vue";
import { useAiCheckScan } from "../components/ai-check/useAiCheckScan";

const props = defineProps({
  uuid: { type: String, required: true },
  template: { type: Object, default: () => ({}) },
  documents: { type: Array, default: () => [] },
  editable: { type: Boolean, default: false },
});

const emit = defineEmits(["refresh-documents"]);
const { t, locale } = useI18n();
const confirm = useConfirm();
const store = useStore();
const userUuid = computed(() => store.getters.get_self_user?.uuid);

const ACCEPT = ".pdf,.docx,.doc,.xls,.jpg,.jpeg,.png,.bmp";
const MAX_SIZE_MB = 10;

const loading = ref(false);
const uploading = ref(false);

const pasteActive = ref(false);
const dragActive = ref(false);
const dragCounter = ref(0);

const documentList = ref([]);
const documentTypes = ref([]);
const fileInput = ref(null);

const aiCheckOpen = ref(false);

const {
  state: aiCheckState,
  progress: aiCheckProgress,
  feedEvents: aiCheckFeedEvents,
  uiData: aiCheckData,
  lastCheckedAt: aiLastCheckedAt,
  addedDocsCount: aiAddedDocsCount,
  removedDocsCount: aiRemovedDocsCount,
  topCriticalSignals: aiTopIssues,
  isInitialLoading: aiInitialLoading,
  errorMessage: aiCheckError,
  start: startAiCheck,
  restart: restartAiCheck,
  cancel: cancelAiCheck,
} = useAiCheckScan({
  initialState: "unchecked",
  txId: () => props.uuid,
  locale: () => locale.value,
  userUuid: () => userUuid.value,
  documentList: () => documentList.value,
});

function openAiDialog() {
  aiCheckOpen.value = true;
}
function notifyAiRunning() {
  toast.info(t("AI-проверка запущена"), {
    description: t(
      "Обычно занимает около минуты. Уведомление придёт когда проверка завершится — можно продолжать работу."
    ),
  });
}
function onStartAiCheck() {
  startAiCheck();
  notifyAiRunning();
}
function onRestartAiCheck() {
  // Перепроверка — без toast, статус виден в баннере (running с прогрессом).
  restartAiCheck();
}
function onAiCheckStart() {
  startAiCheck();
  notifyAiRunning();
}
function onAiCheckCancel() {
  // Не отменяем фоновую проверку — просто закрываем модалку.
  // Состояние running/triggering сохраняется в баннере.
  aiCheckOpen.value = false;
}
function onAiCheckRestart() {
  // Из модалки — тоже без toast.
  restartAiCheck();
}
function onAiCheckIgnore() {
  aiCheckOpen.value = false;
}
function onAiCheckFix() {
  aiCheckOpen.value = false;
}

const actions = computed(() =>
  props.editable ? ["download", "remove"] : ["download"]
);

const options = computed(() => [
  {
    value: "INDIVIDUAL",
    label: t("Физ лицо"),
    disabled: !props.template?.document_fl_type_codes?.length,
    selectable: false,
    children: (props.template?.document_fl_type_codes || []).map((code) => ({
      value: code,
      label: getLabel(code),
    })),
  },
  {
    value: "LEGAL",
    label: t("Юр лицо"),
    disabled: !props.template?.document_ul_type_codes?.length,
    selectable: false,
    children: (props.template?.document_ul_type_codes || []).map((code) => ({
      value: code,
      label: getLabel(code),
    })),
  },
]);

function getLabel(code) {
  return documentTypes.value.find((d) => d.code === code)?.name || code;
}

async function loadDocumentTypes() {
  try {
    const data = await getDirectoryItem("types_of_documents");
    if (data?.success && data.payload) {
      documentTypes.value = data.payload?.children || [];
    }
  } catch (e) {
    console.error("loadDocumentTypes error:", e);
  }
}

async function fetchDocuments({ silent = false } = {}) {
  if (!props.uuid) return;
  if (!silent) loading.value = true;
  try {
    const data = await getApplicationDocuments(props.uuid);
    if (data?.success) {
      documentList.value = extractItems(data.payload);
    }
  } finally {
    if (!silent) loading.value = false;
  }
}

function validateFile(file) {
  const valid = ACCEPT.split(",").some((ext) =>
    file.name.toLowerCase().endsWith(ext)
  );
  if (!valid) {
    toast.error(t("Недопустимый тип файла"));
    return false;
  }
  if (file.size / 1024 / 1024 > MAX_SIZE_MB) {
    toast.error(t("Файл превышает {n} МБ", { n: MAX_SIZE_MB }));
    return false;
  }
  return true;
}

async function uploadFile(file) {
  if (!validateFile(file)) return;
  uploading.value = true;
  try {
    const formData = new FormData();
    formData.append("file", file);
    const data = await uploadApplicationDocument(props.uuid, formData);
    if (data?.success) {
      await fetchDocuments({ silent: true });
      emit("refresh-documents");
    }
  } finally {
    uploading.value = false;
  }
}

function openFileDialog() {
  fileInput.value?.click();
}

async function onFileChange(event) {
  const files = event.target.files || [];
  for (const file of files) {
    await uploadFile(file);
  }
  event.target.value = "";
}

function handlePaste(event) {
  if (!pasteActive.value) return;
  const items = event.clipboardData?.items || [];
  for (const item of items) {
    if (item.kind === "file") {
      const file = item.getAsFile();
      if (file) uploadFile(file);
    }
  }
}

function activatePaste() {
  pasteActive.value = true;
}

function deactivatePaste() {
  pasteActive.value = false;
}

function onDragEnter() {
  dragCounter.value++;
  dragActive.value = true;
}

function onDragLeave() {
  dragCounter.value--;
  if (dragCounter.value <= 0) {
    dragActive.value = false;
    dragCounter.value = 0;
  }
}

function onDrop(event) {
  dragCounter.value = 0;
  dragActive.value = false;
  const files = event.dataTransfer?.files || [];
  for (const file of files) {
    uploadFile(file);
  }
}

async function downloadFile(file) {
  await downloadFileSecure(file);
}

async function removeFile(file) {
  try {
    await confirm(t("Вы действительно хотите удалить файл?"), t("Внимание"), {
      confirmButtonText: t("Да"),
      cancelButtonText: t("Отмена"),
    });
    const id = file.uuid || file.id;
    if (!id) return;
    const data = await deleteApplicationDocument(props.uuid, id);
    if (data?.success) {
      await fetchDocuments({ silent: true });
      emit("refresh-documents");
    }
  } catch {
    // отменено
  }
}

async function updateDocumentType(file, code) {
  if (!code || code?.length < 1) return;
  const id = file.uuid || file.id;
  if (!id) return;
  await updateApplicationDocument(props.uuid, id, {
    document_type_code: code[1],
  });
  await fetchDocuments({ silent: true });
}

watch(
  () => props.documents,
  (val) => {
    if (Array.isArray(val) && val.length) {
      documentList.value = val;
    }
  }
);

loadDocumentTypes();

onMounted(() => {
  if (props.documents?.length) {
    documentList.value = props.documents;
  } else {
    fetchDocuments();
  }
});
</script>

<template>
  <div class="template">
    <div class="block">
      <AiCheckDialog
        v-model:open="aiCheckOpen"
        :data="aiCheckData"
        :state="aiCheckState"
        :progress="aiCheckProgress"
        :feed-events="aiCheckFeedEvents"
        :is-stale="aiCheckState === 'stale'"
        :new-docs-count="aiAddedDocsCount"
        :removed-docs-count="aiRemovedDocsCount"
        @start="onAiCheckStart"
        @cancel="onAiCheckCancel"
        @restart="onAiCheckRestart"
        @ignore="onAiCheckIgnore"
        @fix="onAiCheckFix"
      />
      <div class="flex flex-col w-full gap-8">
        <div v-if="editable" class="flex flex-col w-full gap-4">
          <h3 class="text-muted-foreground text-sm font-medium">
            {{ $t("Загрузка документов") }}
          </h3>

          <div
            class="upload-wrapper"
            tabindex="0"
            :class="{
              'upload-hover': pasteActive,
              'upload-drag': dragActive,
            }"
            @focusin="activatePaste"
            @focusout="deactivatePaste"
            @paste="handlePaste"
            @dragenter.prevent="onDragEnter"
            @dragover.prevent
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop"
          >
            <input
              ref="fileInput"
              type="file"
              multiple
              class="hidden-upload"
              :accept="ACCEPT"
              @change="onFileChange"
            />

            <div class="upload-card">
              <Button
                variant="outline"
                size="icon"
                :disabled="uploading"
                @click.stop="openFileDialog"
              >
                <svg-icon name="huge-export-attachment" class="w-5 h-5" />
              </Button>

              <div class="upload-text">
                <div class="upload-text-main">
                  <div class="upload-link" @click.stop="openFileDialog">
                    {{ $t("Нажмите для загрузки") }}
                  </div>
                  <div class="upload-text-or">
                    {{ $t("или перенесите файлы в эту область") }}
                  </div>
                </div>
                <p class="upload-formats">PDF, DOCX, DOC, XLS, JPG, PNG, BMP</p>
              </div>
            </div>
          </div>
        </div>

        <div v-if="loading" class="flex justify-center items-center py-8">
          <Spinner size="sm" />
        </div>

        <Empty
          v-else-if="!documentList?.length"
          :description="$t('Вложения отсутствуют')"
        />

        <div v-else class="flex flex-col w-full gap-4">
          <AiCheckBanner
            :state="aiCheckState"
            :progress="aiCheckProgress"
            :critical-count="
              (aiCheckData.score.criticalCount || 0) +
              (aiCheckData.score.warningCount || 0)
            "
            :refusal-risk="aiCheckData.score.refusalProb || 0"
            :last-checked-at="aiLastCheckedAt"
            :error-message="aiCheckError"
            :new-docs-count="aiAddedDocsCount"
            :removed-docs-count="aiRemovedDocsCount"
            :documents-count="documentList.length"
            :top-issues="aiTopIssues"
            :initial-loading="aiInitialLoading"
            @open="openAiDialog"
            @start="onStartAiCheck"
            @restart="onRestartAiCheck"
            @retry="onStartAiCheck"
            @cancel="cancelAiCheck"
          />

          <div
            class="grid gap-4"
            :class="editable ? 'docs-grid' : 'grid-cols-1'"
          >
            <h3 class="text-muted-foreground text-sm font-medium">
              {{ $t("Загруженные документы") }}
            </h3>
            <h3
              v-if="editable"
              class="text-muted-foreground text-sm font-medium hide-on-mobile"
            >
              {{ $t("Тип документа") }}
            </h3>
          </div>

          <div
            v-for="file in documentList"
            :key="file.uuid || file.id"
            class="grid gap-4"
            :class="editable ? 'docs-grid' : 'grid-cols-1'"
          >
            <AttachmentItem
              :file="file"
              :actions="actions"
              @remove="removeFile(file)"
              @download="downloadFile(file)"
            />

            <div v-if="editable" class="w-full">
              <div
                class="w-full p-4 bg-card rounded-[var(--radius)] border border-border"
              >
                <Cascader
                  v-model="file.document_type_code"
                  class="w-full"
                  :options="options"
                  :props="{ expandTrigger: 'click' }"
                  :show-all-levels="false"
                  clearable
                  :placeholder="$t('Выберите тип документа')"
                  @change="(code) => updateDocumentType(file, code)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.template {
  @apply flex h-full w-full overflow-x-hidden gap-5;
  .block {
    @apply p-8 h-full overflow-auto rounded-[var(--radius)] flex flex-col gap-6 w-1/2;
    background-color: hsl(var(--background));
  }

  .upload-wrapper {
    @apply relative rounded-[var(--radius)] transition-all outline outline-1 outline-offset-[-1px] outline-border;
  }

  .upload-card {
    @apply self-stretch w-full px-6 py-5 bg-muted rounded-[var(--radius)]
    flex flex-col items-center gap-3 text-center;
  }

  .upload-hover .upload-card {
    @apply outline outline-1 outline-primary outline-offset-[-2px];
  }

  .upload-drag .upload-card {
    @apply outline outline-1 outline-primary outline-offset-[-2px] bg-primary/10;
  }

  .upload-text {
    @apply flex flex-col items-center gap-1 max-w-full;
  }

  .upload-text-main {
    @apply text-sm leading-5 text-foreground text-center;
  }

  .upload-link {
    @apply inline text-muted-foreground font-semibold cursor-pointer mr-1;

    &:hover {
      @apply underline;
    }
  }

  .upload-text-or {
    @apply inline text-foreground;
  }

  .upload-formats {
    @apply text-muted-foreground text-xs leading-4 text-center m-0;
  }

  .docs-grid {
    @apply grid-cols-2;
  }

  .hidden-upload {
    display: none;
  }

  /* ===== Responsive ===== */
  @media (max-width: 1500px) {
    .block {
      @apply w-full;
    }
  }

  @media (max-width: 768px) {
    .block {
      @apply p-4 gap-4;
    }

    .upload-card {
      @apply px-4 py-4 gap-2;
    }

    .docs-grid {
      @apply grid-cols-1;
    }

    .hide-on-mobile {
      @apply hidden;
    }
  }

  @media (max-width: 480px) {
    .block {
      @apply p-3 gap-3;
    }

    .upload-card {
      @apply px-3 py-3;
    }

    .upload-text-main {
      @apply text-[13px];
    }

    .upload-formats {
      @apply text-[10.5px];
    }
  }
}
</style>
